/**
 * Synced people from a connected host product (product interplay HF2, 2026-10-26).
 *
 * Screens for the AnooreHR backend's H7 (invites) and H8 (ownership, proposals, the HR queue) API.
 * Every route answers 404 while the server's INTERPLAY_PEOPLE_ENABLED (H8) or
 * INTERPLAY_INVITES_ENABLED (H7) is off; the screens treat that 404 as "nothing to show".
 *
 * Errors are typed: `{ detail: { code, message, ...extra } }` (see `interplayError`). Other
 * products are only ever named by data (`product_code` here, the name from Console discovery).
 */
import type { AxiosRequestConfig } from 'axios';
import apiClient from '@/api/client';

/** Every screen here shows its own typed error in place: no parallel global toast. */
const quiet = (cfg: AxiosRequestConfig = {}): AxiosRequestConfig =>
  ({ ...cfg, _silentOnError: true }) as AxiosRequestConfig;

// ─────────────────────────────────────────────────────────────── ownership (H8) ──

/** One owner per host account. Data only: never a product name. */
export interface HostOwner {
  capability: string;
  product_code: string;
  host_account_id: string;
  label_key?: string;
}

/** `GET /hr/interplay/employees/{id}/ownership` and `GET /employee/interplay/ownership`. */
export interface Ownership {
  employee_id: string;
  referenced: boolean;
  owners: HostOwner[];
  /** Employee columns the host owns (first_name, middle_name, last_name, work_email, phone,
   *  avatar_url, department_id, designation) — empty when not referenced. */
  locked_fields: string[];
  needs_hr_completion: boolean;
  completion_rule?: string;
  pending_proposals: number;
}

/** The host-owned employee columns a proposal may change (H8 `ProposeChange`). */
export interface ProposeChanges {
  first_name?: string;
  middle_name?: string | null;
  last_name?: string;
  work_email?: string | null;
  phone?: string | null;
  avatar_url?: string | null;
  designation?: string | null;
  department_id?: string | null;
}

export interface ProposeChangeBody {
  changes: ProposeChanges;
  note?: string;
}

// ─────────────────────────────────────────────────────────────────── queue (H8) ──

export type QueueView = 'held' | 'proposals' | 'exits' | 'all';
export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'withdrawn' | 'expired';
export type ExitMode = 'exited' | 'notice_period' | 'notice_end_set' | 'exit_planned' | 'already_exited';

export interface QueueEmployee {
  id: string;
  employee_ref: string | null;
  first_name: string | null;
  last_name: string | null;
  work_email: string | null;
  email: string | null;
  designation: string | null;
  employment_status: string | null;
}

export interface HeldPerson {
  name?: { given_name?: string | null; family_name?: string | null; middle_name?: string | null };
  work_email?: string | null;
  phone?: string | null;
  photo_url?: string | null;
  job_title?: string | null;
  unit_ref?: string | null;
  start_date_hint?: string | null;
}

export interface QueueResolution {
  decision: string | null;
  resolved_by: string | null;
  action: string | null;
  note: string | null;
  resolver_user_id: string | null;
  outcome: {
    exit?: { last_working_day: string; mode: ExitMode };
    employee_id?: string;
    result?: string;
    [k: string]: unknown;
  } | null;
}

export interface QueueItem {
  id: string;
  view: Exclude<QueueView, 'all'>;
  kind: 'field_change' | 'exit' | 'new_person';
  direction: 'host_to_provider' | 'provider_to_host';
  status: ProposalStatus;
  employee_id: string | null;
  employee: QueueEmployee | null;
  host: { product_code: string; account_id: string };
  person_ref: string | null;
  proposed_by: string | null;
  note: string | null;
  created_at: string | null;
  resolved_at: string | null;
  resolution: QueueResolution | null;
  /** held */
  held?: HeldPerson;
  reason?: string | null;
  /** exits */
  exit?: { reason_code?: string | null; last_working_day_hint?: string | null; [k: string]: unknown } | null;
  /** field changes */
  proposed?: Record<string, unknown> | null;
  fields?: string[] | null;
}

export interface QueuePage {
  items: QueueItem[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export type ResolveAction = 'same_person' | 'new_person' | 'confirm_exit' | 'reject_exit' | 'withdraw';

export interface ResolveBody {
  action: ResolveAction;
  note?: string;
  exit_date?: string;
  exit_reason?: string;
}

// ───────────────────────────────────────────────────────────────── invites (H7) ──

export type InviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired' | string;

export interface PersonInvite {
  invite_ref: string;
  host_product_code: string;
  person_ref: string;
  status: InviteStatus;
  revoked_reason: string | null;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export interface IssuedInvite {
  invite_ref: string;
  host_product_code: string;
  person_ref: string;
  expires_at: string;
}

export interface InviteResult {
  object: 'person_invite_result';
  employee_id: string;
  user_created: boolean;
  invites: IssuedInvite[];
  superseded: string[];
}

export interface InviteBatch {
  object: 'person_invite_batch';
  counts: { issued: number; refused: number };
  results: Array<
    | { employee_id: string; result: 'issued'; invites: IssuedInvite[] }
    | { employee_id: string; result: 'refused'; error: string }
  >;
  has_more: boolean;
  remaining: number;
  next_cursor: string | null;
}

// ──────────────────────────────────────────────────────────────────────── errors ──

export interface InterplayErrorInfo {
  status: number | null;
  code: string | null;
  message: string | null;
  fields: string[];
  owners: HostOwner[];
}

/** The typed refusal of any of these routes (or of an employee edit / transfer the H8 guard
 *  refused). Network errors and untyped answers come back with `code: null`. */
export function interplayError(err: unknown): InterplayErrorInfo {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const detail = res?.data?.detail;
  const d = detail && typeof detail === 'object' && !Array.isArray(detail) ? (detail as Record<string, unknown>) : null;
  const owners = Array.isArray(d?.owners)
    ? (d!.owners as unknown[]).filter((o): o is HostOwner => !!o && typeof (o as HostOwner).product_code === 'string')
    : d?.owner && typeof (d.owner as HostOwner).product_code === 'string'
      ? [d.owner as HostOwner]
      : [];
  return {
    status: typeof res?.status === 'number' ? res.status : null,
    code: typeof d?.code === 'string' ? d.code : null,
    message: typeof d?.message === 'string' ? d.message : typeof detail === 'string' ? detail : null,
    fields: Array.isArray(d?.fields) ? (d!.fields as unknown[]).filter((f): f is string => typeof f === 'string') : [],
    owners,
  };
}

/** True when the route is simply not there (the server flag is off). */
export function isNotFound(err: unknown): boolean {
  return interplayError(err).status === 404;
}

/**
 * The flag-off 404 only: the route answers FastAPI's plain `{"detail": "Not Found"}` while its
 * server flag is off. A typed 404 (`employee_not_found`, `company_not_selected`, …) is a real
 * answer to show in words, not "turned off".
 */
export function isFlagOff(err: unknown): boolean {
  const e = interplayError(err);
  return e.status === 404 && e.code === null;
}

// ──────────────────────────────────────────────────────────────────────────── api ──

export const interplayPeopleApi = {
  ownership: (employeeId: string) =>
    apiClient.get<Ownership>(`/hr/interplay/employees/${employeeId}/ownership`, quiet()),
  proposeChange: (employeeId: string, body: ProposeChangeBody) =>
    apiClient.post<{ proposals: QueueItem[] }>(`/hr/interplay/employees/${employeeId}/propose-change`, body, quiet()),

  myOwnership: () => apiClient.get<Ownership>('/employee/interplay/ownership', quiet()),
  myProposeChange: (body: ProposeChangeBody) =>
    apiClient.post<{ proposals: QueueItem[] }>('/employee/interplay/propose-change', body, quiet()),

  queue: (params: { view: QueueView; status?: ProposalStatus | 'all'; page?: number; per_page?: number }) =>
    apiClient.get<QueuePage>('/hr/interplay/queue', quiet({ params })),
  queueItem: (id: string) => apiClient.get<QueueItem>(`/hr/interplay/queue/${encodeURIComponent(id)}`, quiet()),
  resolve: (id: string, body: ResolveBody) =>
    apiClient.post<QueueItem | Record<string, unknown>>(`/hr/interplay/queue/${encodeURIComponent(id)}/resolve`, body, quiet()),

  invites: (employeeId: string) =>
    apiClient.get<{ object: 'list'; items: PersonInvite[] }>('/interplay/invites', quiet({ params: { employee_id: employeeId } })),
  invite: (employeeId: string) =>
    apiClient.post<InviteResult>('/interplay/invites', { employee_id: employeeId }, quiet()),
  inviteAll: (cursor?: string | null) =>
    apiClient.post<InviteBatch>('/interplay/invites', cursor ? { all_referenced: true, cursor } : { all_referenced: true }, quiet()),
  revokeInvite: (inviteRef: string) =>
    apiClient.post<PersonInvite>('/interplay/invites/revoke', { invite_ref: inviteRef }, quiet()),
};
